import { MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';
import { ImageWithNotes } from '@brands/services/forms/types/ImageWithNotes';
import { ICase } from '@brands/services/cases/types/ICase';
import { Document, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { parseDocument } from 'htmlparser2';
import linkifyHtml from 'linkify-html';
import React from 'react';
import sanitizeHtml from 'sanitize-html';

import {
  buildOHRReportViewModel,
  OHR_LEGAL_DISCLAIMER,
  OHRReportViewModel,
  ReportField,
  ReportRichText,
} from '../../../OralHealthReport/reportViewModel';

const PRIMARY = '#2E73BD';
const TEXT_DARK = '#1F2937';
const TEXT_MUTED = '#6B7280';
const BORDER = '#E5E7EB';
const SECTION_HEADING_MIN_PRESENCE_AHEAD = 72;
const NOTE_MIN_PRESENCE_AHEAD = 44;

const styles = StyleSheet.create({
  page: {
    paddingTop: 46,
    paddingHorizontal: 46,
    paddingBottom: 28,
    fontSize: 11,
    fontFamily: 'Helvetica',
    color: TEXT_DARK,
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
    width: '100%',
  },

  logoText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: PRIMARY,
  },

  headerRight: {
    alignItems: 'flex-end',
  },

  reportTitle: {
    fontSize: 10,
    color: TEXT_MUTED,
    marginBottom: 2,
  },

  date: {
    fontSize: 10,
    color: TEXT_MUTED,
  },

  body: {},

  section: {
    marginBottom: 22,
  },

  tightSection: {
    marginBottom: 16,
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 7,
    color: TEXT_DARK,
  },

  divider: {
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    marginBottom: 15,
  },

  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },

  column: {
    width: '47%',
  },

  columnThird: {
    width: '30%',
  },

  field: {
    marginBottom: 11,
  },

  label: {
    fontSize: 10,
    color: TEXT_MUTED,
    marginBottom: 3,
    fontWeight: 'bold',
  },

  value: {
    fontSize: 11,
    lineHeight: 1.35,
    color: TEXT_DARK,
  },

  paragraph: {
    fontSize: 11,
    lineHeight: 1.45,
    color: TEXT_DARK,
  },

  additionalInfo: {
    marginBottom: 7,
  },

  infoDate: {
    fontSize: 9,
    color: TEXT_MUTED,
    marginBottom: 2,
    fontWeight: 'bold',
  },

  prescriptionBlock: {
    marginBottom: 20,
  },

  imageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 10,
  },

  imageCard: {
    width: '48%',
    marginBottom: 20,
  },

  patientImage: {
    width: '100%',
    height: 142,
    objectFit: 'cover',
  },

  imageCaption: {
    fontSize: 10,
    lineHeight: 1.35,
    marginTop: 7,
    color: TEXT_DARK,
  },

  educationItem: {
    marginBottom: 9,
  },

  link: {
    fontSize: 11,
    color: PRIMARY,
    lineHeight: 1.35,
  },

  noteItem: {
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },

  noteMeta: {
    fontSize: 9,
    color: TEXT_MUTED,
    marginBottom: 4,
    fontWeight: 'bold',
  },

  treatmentItem: {
    marginBottom: 14,
    paddingBottom: 4,
  },

  disclaimer: {
    marginTop: 8,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: BORDER,
  },

  disclaimerTitle: {
    fontSize: 9,
    color: TEXT_MUTED,
    marginBottom: 4,
    fontWeight: 'bold',
  },

  disclaimerText: {
    fontSize: 9,
    lineHeight: 1.35,
    color: TEXT_MUTED,
  },

  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: TEXT_MUTED,
  },

  logoImage: {
    width: 120,
    height: 36,
    objectFit: 'contain',
  },

  poweredByText: {
    fontSize: 8,
    marginTop: 4,
    color: TEXT_MUTED,
  },
});

interface Props {
  thisCase: ICase;
  logoBase64: string;
  images: ImageWithNotes[];
  medicalHistory?: MedicalHistory | null;
  medicalCondition?: MedicalConditions | null;
  pharmacyAddress?: string;
  pharmacyPhoneNumber?: string;
  hideAttestationChecklistCopy?: boolean;
}

const sanitizeDisplayHtml = (html: string): string => {
  const linked = linkifyHtml(html || '', {
    defaultProtocol: 'https',
    attributes: { target: '_blank', rel: 'noopener noreferrer' },
  });

  return sanitizeHtml(linked, {
    allowedTags: ['p', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'br', 'span', 'a'],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
      span: ['class'],
      li: ['data-list'],
    },
  });
};

const renderHtmlToPdf = (html: string) => {
  const dom = parseDocument(html);
  let orderedIndex = 1;

  const renderNodes = (nodes: any[], parentType?: string): any[] =>
    nodes.flatMap((node, index) => {
      if (node.type === 'text') {
        return <Text key={index}>{node.data}</Text>;
      }

      if (node.name === 'strong') {
        return (
          <Text key={index} style={{ fontWeight: 'bold' }}>
            {renderNodes(node.children)}
          </Text>
        );
      }

      if (node.name === 'em') {
        return (
          <Text key={index} style={{ fontStyle: 'italic' }}>
            {renderNodes(node.children)}
          </Text>
        );
      }

      if (node.name === 'u') {
        return (
          <Text key={index} style={{ textDecoration: 'underline' }}>
            {renderNodes(node.children)}
          </Text>
        );
      }

      if (node.name === 'br') {
        return (
          <Text key={index} style={{ marginBottom: 2 }}>
            {' '}
          </Text>
        );
      }

      if (node.name === 'p') {
        return (
          <Text key={index} style={{ marginBottom: 5, lineHeight: 1.45 }}>
            {renderNodes(node.children)}
          </Text>
        );
      }

      if (node.name === 'a') {
        const href = node.attribs?.href || '';
        return (
          <Link key={index} src={href} style={{ color: PRIMARY }}>
            {renderNodes(node.children)}
          </Link>
        );
      }

      if (node.name === 'ul') {
        orderedIndex = 1;
        return (
          <View key={index} style={{ marginBottom: 4 }}>
            {renderNodes(node.children, 'ul')}
          </View>
        );
      }

      if (node.name === 'ol') {
        orderedIndex = 1;
        return (
          <View key={index} style={{ marginBottom: 4 }}>
            {renderNodes(node.children, 'ol')}
          </View>
        );
      }

      if (node.name === 'li') {
        const prefix = parentType === 'ol' ? `${orderedIndex++}. ` : '• ';
        return (
          <Text key={index} style={{ marginBottom: 2, lineHeight: 1.35 }}>
            {prefix}
            {renderNodes(node.children)}
          </Text>
        );
      }

      return renderNodes(node.children || []);
    });

  return <View style={styles.paragraph}>{renderNodes(dom.children)}</View>;
};

const Header = ({
  logoBase64,
  report,
  usesCustomLogo,
}: {
  logoBase64: string;
  report: OHRReportViewModel;
  usesCustomLogo?: boolean;
}): JSX.Element => (
  <View style={styles.header}>
    <View>
      {logoBase64 ? (
        <Image src={logoBase64} style={styles.logoImage} />
      ) : (
        <Text style={styles.logoText}>dentistry.one</Text>
      )}
      {usesCustomLogo && <Text style={styles.poweredByText}>Powered by Dentistry.One</Text>}
    </View>

    <View style={styles.headerRight}>
      <Text style={styles.reportTitle}>Oral Health Report</Text>
      <Text style={styles.date}>{report.headerDate}</Text>
    </View>
  </View>
);

const ReportPage = ({
  children,
  logoBase64,
  report,
  usesCustomLogo,
}: {
  children: React.ReactNode;
  logoBase64: string;
  report: OHRReportViewModel;
  usesCustomLogo?: boolean;
}): JSX.Element => (
  <Page size="LETTER" style={styles.page}>
    <Header logoBase64={logoBase64} report={report} usesCustomLogo={usesCustomLogo} />
    <View style={styles.body}>{children}</View>
    <Text style={styles.footer} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} fixed />
  </Page>
);

const Section = ({
  title,
  children,
  tight = false,
  wrap = true,
  minPresenceAhead = SECTION_HEADING_MIN_PRESENCE_AHEAD,
}: {
  title: string;
  children: React.ReactNode;
  tight?: boolean;
  wrap?: boolean;
  minPresenceAhead?: number;
}): JSX.Element => (
  <View style={tight ? styles.tightSection : styles.section} wrap={wrap}>
    <View wrap={false} minPresenceAhead={minPresenceAhead}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.divider} />
    </View>
    {children}
  </View>
);

const FieldList = ({ fields }: { fields: ReportField[] }): JSX.Element => (
  <View>
    {fields.map((field) => (
      <View key={`${field.label}-${field.value}`} style={styles.field}>
        <Text style={styles.label}>{field.label}</Text>
        <Text style={styles.value}>{field.value}</Text>
      </View>
    ))}
  </View>
);

const TwoColumnFields = ({
  left,
  right,
  third,
}: {
  left: ReportField[];
  right: ReportField[];
  third?: ReportField[];
}): JSX.Element => (
  <View style={styles.row}>
    <View style={third?.length ? styles.columnThird : styles.column}>
      <FieldList fields={left} />
    </View>
    <View style={third?.length ? styles.columnThird : styles.column}>
      <FieldList fields={right} />
    </View>
    {third?.length ? (
      <View style={styles.columnThird}>
        <FieldList fields={third} />
      </View>
    ) : null}
  </View>
);

const RichTextBlock = ({ richText, emptyText = '-' }: { richText: ReportRichText | null; emptyText?: string }) =>
  richText ? renderHtmlToPdf(sanitizeDisplayHtml(richText.html)) : <Text style={styles.value}>{emptyText}</Text>;

const CdtCodes = ({ report }: { report: OHRReportViewModel }): JSX.Element => (
  <Section title="CDT codes" tight>
    {report.cdtCodes.length > 0 ? (
      <View>
        {report.cdtCodes.map((cdt) => (
          <View key={`${cdt.code}-${cdt.nomenclature}`} style={{ flexDirection: 'row', marginBottom: 4 }}>
            <Text style={styles.value}>{cdt.code}: </Text>
            <Text style={styles.value}>{cdt.nomenclature}</Text>
          </View>
        ))}
      </View>
    ) : (
      <Text style={styles.value}>-</Text>
    )}
  </Section>
);

const OHRPdfDocument: React.FC<Props> = ({
  thisCase,
  logoBase64,
  images,
  medicalHistory,
  medicalCondition,
  pharmacyAddress,
  pharmacyPhoneNumber,
  hideAttestationChecklistCopy = false,
}) => {
  const report = buildOHRReportViewModel({
    thisCase,
    images,
    medicalHistory,
    medicalCondition,
    pharmacyAddress,
    pharmacyPhoneNumber,
  });

  const pageProps = {
    logoBase64,
    report,
    usesCustomLogo: thisCase.organization?.uses_custom_logo,
  };

  return (
    <Document>
      <ReportPage {...pageProps}>
        <Section title="Appointment details">
          <TwoColumnFields left={report.appointmentLeft} right={report.appointmentRight} />
        </Section>

        <Section title="Care team">
          <TwoColumnFields left={report.careTeamLeft} right={report.careTeamRight} third={report.careTeamCollector} />
        </Section>

        <Section title="Patient details">
          <TwoColumnFields left={report.patientLeft} right={report.patientRight} />
        </Section>
      </ReportPage>

      <ReportPage {...pageProps}>
        <Section title="Visit summary">
          <View style={styles.row}>
            <View style={styles.column}>
              <FieldList fields={report.visitSummaryLeft} />
            </View>
            <View style={styles.column}>
              <View style={styles.field}>
                <Text style={styles.label}>Other comments to provider</Text>
                {report.additionalInfo.length > 0 ? (
                  report.additionalInfo.map((info) => (
                    <View key={info.id} style={styles.additionalInfo}>
                      {info.date && <Text style={styles.infoDate}>{info.date}</Text>}
                      <Text style={styles.value}>{info.text}</Text>
                    </View>
                  ))
                ) : (
                  <Text style={styles.value}>-</Text>
                )}
              </View>

              {report.providerComment && (
                <View style={styles.field}>
                  <Text style={styles.label}>Additional comments from provider</Text>
                  <RichTextBlock richText={report.providerComment} />
                </View>
              )}
            </View>
          </View>
        </Section>

        {report.assessment && (
          <Section title="Assessment" tight>
            <RichTextBlock richText={report.assessment} />
          </Section>
        )}

        <CdtCodes report={report} />
      </ReportPage>

      {report.prescriptions.length > 0 && (
        <ReportPage {...pageProps}>
          <Section title="Prescriptions">
            {report.prescriptions.map((prescription) => (
              <View key={prescription.id} style={styles.prescriptionBlock}>
                <FieldList
                  fields={[
                    { label: 'Drug name', value: prescription.drug },
                    { label: 'Instructions', value: prescription.instructions },
                    { label: 'Prescription date', value: prescription.prescriptionDate },
                    { label: 'Dispense', value: prescription.dispense },
                    { label: 'Refills', value: prescription.refills },
                    ...prescription.pharmacy,
                  ].filter((field) => field.value)}
                />
              </View>
            ))}
          </Section>
        </ReportPage>
      )}

      {report.images.length > 0 && (
        <ReportPage {...pageProps}>
          <Section title="Images">
            <View style={styles.imageGrid}>
              {report.images.map((image) => (
                <View key={image.id} style={styles.imageCard}>
                  <Image src={image.src} style={styles.patientImage} />
                  {image.notes && <Text style={styles.imageCaption}>{image.notes}</Text>}
                </View>
              ))}
            </View>
          </Section>
        </ReportPage>
      )}

      <ReportPage {...pageProps}>
        <Section title="Recommended treatment">
          <View style={styles.field}>
            <Text style={styles.label}>Description</Text>
            <RichTextBlock richText={report.recommendedTreatment} />
          </View>
        </Section>

        {report.treatments.length > 0 && (
          <Section title="Treatment">
            {report.treatments.map((treatment) => (
              <View key={treatment.id} style={styles.treatmentItem}>
                <FieldList fields={treatment.fields} />
                {treatment.description && (
                  <View style={styles.field}>
                    <Text style={styles.label}>Description</Text>
                    <Text style={styles.value}>{treatment.description}</Text>
                  </View>
                )}
              </View>
            ))}
          </Section>
        )}

        {report.followUpColumns.length > 0 && (
          <Section title="Recommended follow-up care">
            <View style={styles.row}>
              {report.followUpColumns.map((followUp) => (
                <View key={followUp.id} style={styles.column}>
                  <View style={styles.field}>
                    <Text style={styles.label}>{followUp.recommendedLabel}</Text>
                    <Text style={styles.value}>{followUp.recommendedValue}</Text>
                  </View>
                  <FieldList fields={followUp.fields} />
                </View>
              ))}
            </View>
          </Section>
        )}

        {report.fulfillmentApproval && (
          <Section title="Fulfillment approval">
            <View style={styles.field}>
              <Text style={styles.label}>Decision</Text>
              <Text style={styles.value}>{report.fulfillmentApproval.decision}</Text>
            </View>
            {report.fulfillmentApproval.notes && (
              <View style={styles.field}>
                <Text style={styles.label}>Notes</Text>
                <Text style={styles.value}>{report.fulfillmentApproval.notes}</Text>
              </View>
            )}
          </Section>
        )}

        {report.educations.length > 0 && (
          <Section title="Educational resources">
            {report.educations.map((education) => (
              <View key={education.id} style={styles.educationItem}>
                <Text style={styles.label}>{education.title}</Text>
                <Link src={education.url} style={styles.link}>
                  {education.url}
                </Link>
              </View>
            ))}
          </Section>
        )}

        {report.notes.length > 0 && (
          <Section title="Notes">
            {report.notes.map((note) => (
              <View key={note.id} style={styles.noteItem} minPresenceAhead={NOTE_MIN_PRESENCE_AHEAD}>
                <Text style={styles.noteMeta}>{note.meta}</Text>
                {renderHtmlToPdf(sanitizeDisplayHtml(note.html))}
              </View>
            ))}
          </Section>
        )}

        {report.providerAttestation && (
          <Section title="Provider attestation" wrap={false} minPresenceAhead={64}>
            {!hideAttestationChecklistCopy && <Text style={styles.label}>Compliance checklist confirmed</Text>}
            <Text style={styles.value}>
              {report.providerAttestation.date} | {report.providerAttestation.providerName} (provider)
            </Text>
          </Section>
        )}

        <View style={styles.disclaimer}>
          <Text style={styles.disclaimerTitle}>DISCLAIMER</Text>
          <Text style={styles.disclaimerText}>{OHR_LEGAL_DISCLAIMER}</Text>
        </View>
      </ReportPage>
    </Document>
  );
};

export default OHRPdfDocument;
